import React, { FC } from "react";
import InlineSvg from "Shared/components/inline-svg";
import InputCheckbox, {
    InputCheckboxChanged,
} from "Shared/components/input-checkbox";
import { directions } from "Shared/util/sort";
import { ColumnTypes, DataTableColumn, SelectableRowHeader } from "./data-table";
import styles from "./data-table.css";

type DataTableHeaderProps = {
    columns: DataTableColumn[];
    handleSorting: (key: DataTableColumn["key"]) => void;
    selectableRowHeader: SelectableRowHeader;
    isInvertedColumn: (type: ColumnTypes | undefined) => boolean;
    numberOfRows: number;
    numberOfSelectedRows: number;
    numberOfValidRows: number;
    onAllRowsDeselected: () => void;
    onAllRowsSelected: () => void;
    sortDirection?: string;
    sortKey?: string | undefined;
    sorting?: boolean | undefined;
    useRowInformation: boolean;
    useSelectableRows: boolean;
};

export const DataTableHeader: FC<DataTableHeaderProps> = ({
    columns,
    selectableRowHeader,
    sorting,
    sortKey,
    sortDirection,
    handleSorting,
    isInvertedColumn,
    numberOfRows,
    numberOfSelectedRows,
    numberOfValidRows,
    useSelectableRows,
    onAllRowsSelected,
    onAllRowsDeselected,
    useRowInformation,
}) => {
    const handleAllSelected = ({ isChecked }: InputCheckboxChanged) => {
        if (isChecked) {
            onAllRowsSelected();
        } else {
            onAllRowsDeselected();
        }
    };

    const getSortDirectionArrowClass = ({ key }: DataTableColumn) => {
        if (key === sortKey && sortDirection === directions.ASC) {
            return styles.toggleArrowUp;
        }
        if (key === sortKey && sortDirection === directions.DESC) {
            return styles.toggleArrowDown;
        }
        return styles.toggleArrowHidden;
    };

    return (
        <tr>
            {useSelectableRows && (
                <th>
                    {selectableRowHeader.hideSelectAll ? (
                        <>{selectableRowHeader.title}</>
                    ) : (
                        <InputCheckbox
                            checked={
                                numberOfSelectedRows > 0 &&
                                numberOfSelectedRows === numberOfValidRows
                            }
                            onChange={handleAllSelected}
                            className={styles.customCheckbox}
                            disabled={
                                numberOfRows <= 0 || numberOfValidRows <= 0
                            }
                        />
                    )}
                </th>
            )}
            {columns.map((column) => (
                <th
                    className={
                        isInvertedColumn(column.type) ? styles.alignRight : ""
                    }
                    key={column.key}
                >
                    <button
                        onClick={() => handleSorting(column.key)}
                        type="button"
                    >
                        <div
                            className={`${styles.headerContainer} ${
                                isInvertedColumn(column.type)
                                    ? styles.inverted
                                    : ""
                            }`}
                        >
                            <div>{column.title}</div>
                            {sorting && (
                                <InlineSvg
                                    src="/caret-up.svg"
                                    className={`${
                                        styles.sortDirectionToggler
                                    } ${getSortDirectionArrowClass(column)}`}
                                />
                            )}
                        </div>
                    </button>
                </th>
            ))}
            {useRowInformation && (
                <th className={styles.rowInformation}>&nbsp;</th>
            )}
        </tr>
    );
};
